"use client";

import * as React from "react";

import {
  OVERVIEW_EASE,
  prefersReducedMotion,
} from "@/components/site/sections/overview-board/styles";

type FlipFrame = {
  rects: Map<string, DOMRect>;
  nodes: Map<string, HTMLElement>;
};

const MOVE_MS = 360;
const ENTER_MS = 300;
const EXIT_MS = 160;

function flipChildren(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>("[data-flip]")];
}

/**
 * FLIP for a filtered list inside `root`: call the returned `capture()` right
 * before the state change. After the re-render, children marked
 * `data-flip="<id>"` that stayed glide from their old place, new ones fade in,
 * and removed ones fade out as inert copies over the spot they left. Reduced
 * motion keeps only the fades. `root` must be positioned so the copies land
 * in place.
 */
export function useFlipLayout(
  root: React.RefObject<HTMLElement | null>,
  key: unknown,
): () => void {
  const frame = React.useRef<FlipFrame | null>(null);

  const capture = () => {
    const element = root.current;

    if (!element) {
      return;
    }

    const children = flipChildren(element);
    frame.current = {
      rects: new Map(
        children.map((child) => [child.dataset.flip ?? "", child.getBoundingClientRect()]),
      ),
      nodes: new Map(children.map((child) => [child.dataset.flip ?? "", child])),
    };
  };

  React.useLayoutEffect(() => {
    const before = frame.current;
    const element = root.current;
    frame.current = null;

    if (!before || !element || typeof element.animate !== "function") {
      return;
    }

    const reduce = prefersReducedMotion();
    const present = new Set<string>();
    let entering = 0;

    for (const child of flipChildren(element)) {
      const id = child.dataset.flip ?? "";
      const previous = before.rects.get(id);
      const now = child.getBoundingClientRect();
      present.add(id);

      if (!previous) {
        child.animate(
          reduce
            ? [{ opacity: 0 }, { opacity: 1 }]
            : [
                { opacity: 0, transform: "translateY(8px) scale(0.98)" },
                { opacity: 1, transform: "none" },
              ],
          {
            duration: ENTER_MS,
            delay: Math.min(entering++, 5) * 30,
            easing: OVERVIEW_EASE,
            fill: "backwards",
          },
        );
        continue;
      }

      const dx = previous.left - now.left;
      const dy = previous.top - now.top;

      if (!reduce && (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5)) {
        child.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
          { duration: MOVE_MS, easing: OVERVIEW_EASE },
        );
      }
    }

    const box = element.getBoundingClientRect();

    for (const [id, rect] of before.rects) {
      const node = before.nodes.get(id);

      if (present.has(id) || !node) {
        continue;
      }

      const ghost = node.cloneNode(true) as HTMLElement;
      ghost.removeAttribute("data-flip");
      ghost.setAttribute("aria-hidden", "true");
      ghost.setAttribute("inert", "");
      Object.assign(ghost.style, {
        position: "absolute",
        margin: "0",
        left: `${rect.left - box.left - element.clientLeft + element.scrollLeft}px`,
        top: `${rect.top - box.top - element.clientTop + element.scrollTop}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        pointerEvents: "none",
        scrollSnapAlign: "none",
        zIndex: "0",
      });
      element.appendChild(ghost);
      const remove = () => ghost.remove();
      ghost
        .animate(
          reduce
            ? [{ opacity: 1 }, { opacity: 0 }]
            : [{ opacity: 1 }, { opacity: 0, transform: "scale(0.96)" }],
          { duration: EXIT_MS, easing: "ease-in", fill: "forwards" },
        )
        .finished.then(remove, remove);
    }
  }, [root, key]);

  return capture;
}
